import Header from "../../common/Header";
import Footer from "../../common/Footer";
import darkLogo from "../../../images/logo-black.png";
import lightLogo from "../../../images/logo-white.png";
import { Link } from "react-router-dom";
import {
  RiShieldLine,
  RiLockLine,
  RiInformationLine,
  RiFilePaper2Line,
  RiErrorWarningLine,
} from "react-icons/ri";

const PrivacyAndCookies = ({ mode = "light" }) => {
  return (
    <div>
      <div className="relative container mx-auto max-w-screen-2xl">
        <Header mode="light" className="border-b border-gray-3" />

        <div className="flex flex-col mt-10 md:mt-[54px] mb-8 md:mb-[76px] px-6 md:px-[48px] lg:px-[128px]">
          <h1 className="font-[900] md:font-extrabold text-[32px] md:text-[50px] leading-[38px] md:leading-[52px] text-black text-center mb-6 md:mb-10">
            Privacy Policy & Cookies
          </h1>

          <div className="flex flex-col gap-6 md:gap-8 mb-10">
            {/* Introduction */}
            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-blue-500">
              <h2 className="font-bold text-xl md:text-2xl mb-3 text-gray-800">
                Introduction
              </h2>
              <p className="font-normal text-sm md:text-base leading-[20px] md:leading-[24px] text-gray-700">
                At Unspokenn, we are committed to protecting your privacy and
                securing your personal information. This Privacy Policy explains
                how we collect, use, disclose, and safeguard your information
                when you use our anonymous reporting platform. Please read this
                privacy policy carefully. If you do not agree with the terms of
                this privacy policy, please do not access the platform.
              </p>
            </div>

            {/* Information We Collect */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <RiInformationLine className="w-6 h-6 mr-2 text-blue-600" />
                <h2 className="font-bold text-lg md:text-xl text-gray-800">
                  Information We Collect
                </h2>
              </div>

              <div className="pl-8 space-y-4">
                <div>
                  <h3 className="font-semibold text-base md:text-lg mb-2 text-gray-800">
                    Personal Information
                  </h3>
                  <p className="font-normal text-sm md:text-base leading-[20px] md:leading-[24px] text-gray-700 mb-2">
                    Depending on how you choose to use our platform, we may
                    collect the following types of information:
                  </p>
                  <ul className="list-disc pl-5 font-normal text-sm md:text-base leading-[20px] md:leading-[24px] text-gray-700">
                    <li className="mb-1">
                      <strong>Standard Reporting</strong>: Email address, phone
                      number, and any information you voluntarily provide in
                      your report.
                    </li>
                    <li className="mb-1">
                      <strong>Anonymous Reporting</strong>: Only an email
                      address (used solely to provide you with tracking
                      information for your report) and the content of your
                      report.
                    </li>
                    <li>
                      <strong>Usage Data</strong>: Information about how you
                      access and use our platform, including your IP address
                      (which is masked and not stored with your report), browser
                      type, access times, and pages viewed.
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-base md:text-lg mb-2 text-gray-800">
                    Cookies and Tracking Technologies
                  </h3>
                  <p className="font-normal text-sm md:text-base leading-[20px] md:leading-[24px] text-gray-700 mb-2">
                    We use cookies and similar tracking technologies to enhance
                    your experience on our platform:
                  </p>
                  <ul className="list-disc pl-5 font-normal text-sm md:text-base leading-[20px] md:leading-[24px] text-gray-700">
                    <li className="mb-1">
                      <strong>Essential Cookies</strong>: Required for the
                      platform to function properly.
                    </li>
                    <li className="mb-1">
                      <strong>Analytical Cookies</strong>: Help us understand
                      how visitors interact with our platform.
                    </li>
                    <li>
                      <strong>Preference Cookies</strong>: Allow the platform to
                      remember choices you make and provide enhanced features.
                    </li>
                  </ul>
                  <p className="font-normal text-sm md:text-base leading-[20px] md:leading-[24px] text-gray-700 mt-2">
                    You can set your browser to refuse all or some browser
                    cookies, but some parts of the platform may not function
                    properly as a result.
                  </p>
                </div>
              </div>
            </div>

            {/* How We Use Your Information */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <RiFilePaper2Line className="w-6 h-6 mr-2 text-blue-600" />
                <h2 className="font-bold text-lg md:text-xl text-gray-800">
                  How We Use Your Information
                </h2>
              </div>

              <div className="pl-8">
                <p className="font-normal text-sm md:text-base leading-[20px] md:leading-[24px] text-gray-700 mb-2">
                  We use the information we collect for various purposes:
                </p>
                <ul className="list-disc pl-5 font-normal text-sm md:text-base leading-[20px] md:leading-[24px] text-gray-700">
                  <li className="mb-1">To provide and maintain our service</li>
                  <li className="mb-1">
                    To identify and resolve technical issues
                  </li>
                  <li className="mb-1">
                    To process and route reports to appropriate recipients
                  </li>
                  <li className="mb-1">
                    To communicate with you about your report status
                  </li>
                  <li className="mb-1">
                    To improve our platform's functionality and user experience
                  </li>
                  <li>
                    To protect our platform against misuse or unauthorized
                    access
                  </li>
                </ul>
              </div>
            </div>

            {/* Data Security */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <RiLockLine className="w-6 h-6 mr-2 text-blue-600" />
                <h2 className="font-bold text-lg md:text-xl text-gray-800">
                  Data Security
                </h2>
              </div>

              <div className="pl-8">
                <p className="font-normal text-sm md:text-base leading-[20px] md:leading-[24px] text-gray-700">
                  We have implemented appropriate technical and organizational
                  security measures designed to protect the security of any
                  personal information we process. However, despite our
                  safeguards and efforts to secure your information, no
                  electronic transmission over the Internet or information
                  storage technology can be guaranteed to be 100% secure.
                </p>
              </div>
            </div>

            {/* Anonymous Reporting */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <RiShieldLine className="w-6 h-6 mr-2 text-blue-600" />
                <h2 className="font-bold text-lg md:text-xl text-gray-800">
                  Anonymous Reporting
                </h2>
              </div>

              <div className="pl-8">
                <p className="font-normal text-sm md:text-base leading-[20px] md:leading-[24px] text-gray-700 mb-2">
                  When you choose to submit an anonymous report:
                </p>
                <ul className="list-disc pl-5 font-normal text-sm md:text-base leading-[20px] md:leading-[24px] text-gray-700">
                  <li className="mb-1">
                    We collect only the minimum information necessary (an email
                    for tracking purposes)
                  </li>
                  <li className="mb-1">
                    We employ technical measures to mask your identity
                  </li>
                  <li className="mb-1">
                    We remove metadata from uploaded files
                  </li>
                  <li>
                    We do not track the source of anonymous reports beyond what
                    is necessary for platform functionality
                  </li>
                </ul>
              </div>
            </div>

            {/* How We Share Your Information */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <RiFilePaper2Line className="w-6 h-6 mr-2 text-blue-600" />
                <h2 className="font-bold text-lg md:text-xl text-gray-800">
                  How We Share Your Information
                </h2>
              </div>

              <div className="pl-8">
                <p className="font-normal text-sm md:text-base leading-[20px] md:leading-[24px] text-gray-700 mb-2">
                  We do not sell your personal information. We may disclose your
                  information in the following circumstances:
                </p>
                <ul className="list-disc pl-5 font-normal text-sm md:text-base leading-[20px] md:leading-[24px] text-gray-700">
                  <li className="mb-1">
                    <strong>Report Recipients</strong>: Information contained in
                    reports is shared with the appropriate authorities or
                    organizations based on the nature of the report.
                  </li>
                  <li className="mb-1">
                    <strong>Service Providers</strong>: We may share information
                    with third-party vendors who provide services on our behalf
                    (all bound by confidentiality obligations).
                  </li>
                  <li className="mb-1">
                    <strong>Legal Requirements</strong>: We may disclose
                    information if required to do so by law or in response to
                    valid requests by public authorities.
                  </li>
                  <li>
                    <strong>Business Transfers</strong>: If we are involved in a
                    merger, acquisition, or sale of assets, your information may
                    be transferred as part of that transaction.
                  </li>
                </ul>
              </div>
            </div>

            {/* Cookie Policy Details */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <RiInformationLine className="w-6 h-6 mr-2 text-blue-600" />
                <h2 className="font-bold text-lg md:text-xl text-gray-800">
                  Cookie Policy Details
                </h2>
              </div>

              <div className="pl-8 space-y-4">
                <div>
                  <h3 className="font-semibold text-base md:text-lg mb-2 text-gray-800">
                    What Are Cookies
                  </h3>
                  <p className="font-normal text-sm md:text-base leading-[20px] md:leading-[24px] text-gray-700">
                    Cookies are small pieces of text sent to your browser when
                    you visit our website. They serve a variety of functions,
                    including enabling us to remember certain information about
                    how you interact with our platform.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-base md:text-lg mb-2 text-gray-800">
                    Types of Cookies We Use
                  </h3>
                  <ul className="list-disc pl-5 font-normal text-sm md:text-base leading-[20px] md:leading-[24px] text-gray-700">
                    <li className="mb-1">
                      <strong>Session Cookies</strong>: Temporary cookies that
                      are deleted when you close your browser.
                    </li>
                    <li className="mb-1">
                      <strong>Persistent Cookies</strong>: Remain on your device
                      for a predetermined period.
                    </li>
                    <li className="mb-1">
                      <strong>First-Party Cookies</strong>: Set by our website.
                    </li>
                    <li>
                      <strong>Third-Party Cookies</strong>: Set by third-party
                      services we use.
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-base md:text-lg mb-2 text-gray-800">
                    Specific Cookies We Use
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Cookie Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Purpose
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Duration
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            XSRF-TOKEN
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            Essential
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            Security - Protects against cross-site request
                            forgery
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            Session
                          </td>
                        </tr>
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            session_id
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            Essential
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            Maintains your session state
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            Session
                          </td>
                        </tr>
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            preferences
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            Preference
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            Stores your platform preferences
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            1 year
                          </td>
                        </tr>
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            analytics_id
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            Analytical
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            Helps us understand how users interact with the
                            platform
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            90 days
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-base md:text-lg mb-2 text-gray-800">
                    Managing Your Cookie Preferences
                  </h3>
                  <p className="font-normal text-sm md:text-base leading-[20px] md:leading-[24px] text-gray-700 mb-2">
                    Most web browsers allow you to manage your cookie
                    preferences. You can:
                  </p>
                  <ul className="list-disc pl-5 font-normal text-sm md:text-base leading-[20px] md:leading-[24px] text-gray-700">
                    <li className="mb-1">Delete cookies from your device</li>
                    <li className="mb-1">
                      Block cookies by activating the setting on your browser
                      that allows you to refuse all or some cookies
                    </li>
                    <li>
                      Set your browser to notify you when you receive a cookie
                    </li>
                  </ul>
                  <p className="font-normal text-sm md:text-base leading-[20px] md:leading-[24px] text-gray-700 mt-2">
                    Please note that if you choose to block all cookies, you may
                    not be able to access all or parts of our platform.
                  </p>
                </div>
              </div>
            </div>

            {/* Your Rights Notice */}
            <div className="bg-gray-50 p-6 rounded-lg mb-8">
              <div className="flex items-center mb-4">
                <RiErrorWarningLine className="w-6 h-6 mr-2 text-blue-600" />
                <h2 className="font-bold text-lg md:text-xl text-gray-800">
                  Your Data Protection Rights
                </h2>
              </div>

              <p className="font-normal text-sm md:text-base leading-[20px] md:leading-[24px] text-gray-700 mb-2">
                Depending on your location, you may have the following rights
                regarding your personal information:
              </p>
              <ul className="list-disc pl-5 font-normal text-sm md:text-base leading-[20px] md:leading-[24px] text-gray-700">
                <li className="mb-1">The right to access your information</li>
                <li className="mb-1">
                  The right to rectify or update your information
                </li>
                <li className="mb-1">
                  The right to erasure (the "right to be forgotten")
                </li>
                <li className="mb-1">The right to restrict processing</li>
                <li className="mb-1">The right to data portability</li>
                <li className="mb-1">The right to object to processing</li>
                <li>
                  Rights in relation to automated decision making and profiling
                </li>
              </ul>
              <p className="font-normal text-sm md:text-base leading-[20px] md:leading-[24px] text-gray-700 mt-2">
                To exercise any of these rights, please contact us using the
                information provided at the end of this policy.
              </p>
            </div>

            {/* Contact Us */}
            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-blue-500">
              <h2 className="font-bold text-lg md:text-xl mb-3 text-gray-800">
                Contact Us
              </h2>
              <p className="font-normal text-sm md:text-base leading-[20px] md:leading-[24px] text-gray-700">
                If you have any questions about this Privacy Policy, please
                contact us:
              </p>
              <ul className="list-disc pl-5 mt-2 font-normal text-sm md:text-base leading-[20px] md:leading-[24px] text-gray-700">
                <li className="mb-1">
                  By email:{" "}
                  <span className="font-medium">info@unspokenn.com</span>
                </li>
                {/* <li>
                  By mail: Unspokenn Privacy Team, [Your Physical Address]
                </li> */}
              </ul>
              <p className="font-medium text-sm md:text-base mt-4 text-gray-700">
                Last Updated: March 20, 2025
              </p>
            </div>
          </div>

          <div className="flex flex-col items-center max-w-[175px] self-center">
            <Link to="/" className="">
              <img
                src={mode === "dark" ? lightLogo : darkLogo}
                className="h-[17px] md:h-[25.32px]"
                alt="unspokenn"
              />
            </Link>

            <p className="font-medium md:font-normal text-[8px] xs:text-xs leading-[11.76px] xs:leading-[14.32px] mt-3 md:mt-[18px] text-center">
              Copyright © UNSPOKEN 2024
              <br /> All Right Reserved
            </p>
          </div>
        </div>
        <Footer mode="dark" />
      </div>
    </div>
  );
};

export default PrivacyAndCookies;
